import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { faUser } from '@fortawesome/pro-duotone-svg-icons';
import { Store } from '@ngxs/store';
import _ from 'lodash';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, shareReplay, switchMap, takeUntil } from 'rxjs/operators';
import { AvatarService } from 'src/app/services/avatar/avatar.service';
import { SamwinAgentListState } from 'src/app/store/samwin/samwin-agent-list/samwin-agent-list.state';
import { SamwinUserStatusSelectors } from 'src/app/store/samwin/samwin-user-status/samwin-user-status.selectors';
import { SamwinUserStatusState } from 'src/app/store/samwin/samwin-user-status/samwin-user-status.state';
import { SessionState } from 'src/app/store/session/session.state';
import { UserStatusInfoRemoteDto } from 'src/generated/signalR/user-status-info-remote-dto';

@Component({
   selector: 'samweb-avatar',
   templateUrl: './avatar.component.html',
   styleUrls: ['./avatar.component.scss'],
})
export class AvatarComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {
   @Input()
   public userId?: number;
   @Input()
   public dn?: string;
   @Input()
   public useSamwinShape = false;
   @Input()
   public useRoundShape = true;
   @Input()
   public showPresenceStatus = true;
   @Output()
   public avatarBackgroundStyleChanged: EventEmitter<string> = new EventEmitter();

   public userIcon = faUser;
   public avatarUrl$: Observable<string>;
   public avatarBackgroundStyle$: Observable<SafeStyle>;
   public statusInfo$: Observable<UserStatusInfoRemoteDto>;

   private dn$ = new BehaviorSubject<string>('');
   private destroy$ = new Subject();

   constructor(private store: Store, private avatarService: AvatarService, private sanitizer: DomSanitizer) {}

   ngOnInit() {
      this.avatarUrl$ = this.dn$.pipe(
         distinctUntilChanged(),
         switchMap((directoryNumber) => {
            if (_.isEmpty(directoryNumber)) {
               return of('');
            }
            return this.avatarService.getUserAvatar(directoryNumber).pipe(
               map((blob: Blob) => {
                  return _.isNil(blob) ? '' : window.URL.createObjectURL(blob);
               }),
               catchError((Error) => {
                  return of('');
               })
            );
         }),
         shareReplay()
      );

      this.avatarBackgroundStyle$ = this.avatarUrl$.pipe(
         map((unsafeUrl: string) => {
            const avatarBackgroundStyle = this.sanitizer.bypassSecurityTrustStyle(`url(${unsafeUrl})`);
            this.avatarBackgroundStyleChanged.emit(unsafeUrl);
            return avatarBackgroundStyle;
         })
      );
   }

   ngOnDestroy() {
      this.destroy$.next();
      this.destroy$.complete();
   }

   ngOnChanges(changes: SimpleChanges) {
      this.update();
   }

   ngAfterViewInit() {
      this.update();
   }

   private update() {
      this.destroy$.next();

      // use the given DN if there is one (no status infos in this case)
      if (!_.isNil(this.dn)) {
         this.statusInfo$ = null;
         this.dn$.next(this.dn);
         return;
      }

      // if we get a user id, show the status of that user, otherwise use the current user
      if (_.isNil(this.userId) || this.userId === this.store.selectSnapshot(SessionState.getAuthenticatedUserId)) {
         this.userId = this.store.selectSnapshot(SessionState.getAuthenticatedUserId);
         // use special selector which is responding faster than the agent list based selector
         if (this.showPresenceStatus) {
            this.statusInfo$ = this.store.select(SamwinUserStatusState.currentStatusInfo);
         }
      } else if (!_.isNil(this.userId) && this.showPresenceStatus) {
         this.statusInfo$ = this.store.select(SamwinUserStatusSelectors.getUserStatusInfoByUserId).pipe(map((fn) => fn(this.userId)));
      }

      this.store
         .select(SamwinAgentListState.getAgentDetailsForUserId)
         .pipe(map((fn) => fn(this.userId)))
         .pipe(
            map((x) => this.dn$.next(x?.directoryNumber)),
            takeUntil(this.destroy$)
         )
         .subscribe();
   }
}
